import React, { useState } from "react";
import { useTeacherStudentDetails, StudentDetail } from "@/hooks/useTeacherStudentDetails";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Users, BookOpen, Clock, RefreshCw, AlertTriangle, XCircle, TrendingUp } from "lucide-react";
import { StudentTeacherHistoryModal } from "./StudentTeacherHistoryModal";

interface TeacherStudentDetailsPanelProps {
  teacherCode: string;
  startDate?: Date;
  endDate?: Date;
}

const statusConfig: Record<StudentDetail['status'], { label: string; variant: "default" | "destructive" | "secondary" | "outline"; className: string }> = {
  renewed: { label: "ต่อคอร์ส", variant: "default", className: "bg-green-500 hover:bg-green-600" },
  depleted: { label: "หมดคอร์ส", variant: "destructive", className: "" },
  nearly_depleted: { label: "ใกล้หมด", variant: "secondary", className: "bg-orange-500 hover:bg-orange-600 text-white" },
  not_renewed: { label: "หมดแล้วไม่ต่อ", variant: "destructive", className: "bg-red-700 hover:bg-red-800" },
  active: { label: "ปกติ", variant: "outline", className: "" },
};

export const TeacherStudentDetailsPanel: React.FC<TeacherStudentDetailsPanelProps> = ({
  teacherCode,
  startDate,
  endDate,
}) => {
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  
  const { data, isLoading, error } = useTeacherStudentDetails({
    teacherCode,
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
    enabled: true,
  });

  if (!startDate || !endDate) {
    return (
      <div className="p-6 bg-muted/30 border-t">
        <p className="text-center text-muted-foreground">กรุณาเลือกช่วงเวลาเพื่อดูรายละเอียด</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 bg-muted/30 border-t space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-muted/30 border-t">
        <p className="text-center text-destructive">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
      </div>
    );
  }

  if (!data || data.students.length === 0) {
    return (
      <div className="p-6 bg-muted/30 border-t">
        <p className="text-center text-muted-foreground">ไม่พบข้อมูลนักเรียนในช่วงเวลานี้</p>
      </div>
    );
  }

  const { summary, students } = data;

  return (
    <div className="p-6 bg-muted/30 border-t space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{summary.total_students}</p>
              <p className="text-xs text-muted-foreground">นักเรียน</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{summary.total_classes}</p>
              <p className="text-xs text-muted-foreground">คลาส</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-8 w-8 text-indigo-500" />
            <div>
              <p className="text-2xl font-bold">{summary.total_hours.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">ชั่วโมง</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <RefreshCw className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{summary.renewed_count}</p>
              <p className="text-xs text-muted-foreground">ต่อคอร์ส</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">{summary.nearly_depleted_count}</p>
              <p className="text-xs text-muted-foreground">ใกล้หมด</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold">{summary.not_renewed_count}</p>
              <p className="text-xs text-muted-foreground">หมดไม่ต่อ</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-emerald-500" />
            <div>
              <p className="text-2xl font-bold">{summary.renewal_rate}%</p>
              <p className="text-xs text-muted-foreground">อัตราต่อคอร์ส</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Renewal Rate Explanation */}
      <div className="text-sm text-muted-foreground bg-background/50 rounded-md p-3 space-y-1">
        <div>
          <strong>อัตราต่อคอร์ส:</strong> {summary.renewed_count} คนจาก {summary.depleted_count} คน 
          (หมดคอร์ส = หมดแล้วต่อ + หมดแล้วไม่ต่อ)
        </div>
        <div>
          <strong>หมายเหตุ:</strong> ยอดซื้อรวม = ยอดต่อคอร์ส (ตั้งแต่ พ.ค. 2025) + ยอดซื้อใหม่ (ตั้งแต่ พ.ย. 2025)
        </div>
      </div>

      {/* Students Table */}
      <div className="rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>รหัส</TableHead>
              <TableHead>ชื่อเล่น</TableHead>
              <TableHead>ชื่อ</TableHead>
              <TableHead className="text-right">คลาส</TableHead>
              <TableHead className="text-right">ชั่วโมง</TableHead>
              <TableHead className="text-right">คงเหลือ</TableHead>
              <TableHead className="text-right">ยอดซื้อรวม</TableHead>
              <TableHead>สถานะ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((student) => {
              const config = statusConfig[student.status];
              return (
                <TableRow 
                  key={student.student_code}
                  className={selectedStudent === student.student_code ? "bg-primary/10" : ""}
                >
                  <TableCell 
                    className="font-mono text-sm text-primary underline cursor-pointer hover:text-primary/80"
                    onClick={() => setSelectedStudent(student.student_code)}
                  >
                    {student.student_code}
                  </TableCell>
                  <TableCell className="font-medium">{student.student_nickname || '-'}</TableCell>
                  <TableCell>{student.student_name || '-'}</TableCell>
                  <TableCell className="text-right">{student.total_classes}</TableCell>
                  <TableCell className="text-right">{student.total_hours.toFixed(1)}</TableCell>
                  <TableCell className="text-right">
                    {student.current_hours !== null ? student.current_hours.toFixed(1) : '-'}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {student.total_purchase != null 
                      ? <span className="font-medium">{new Intl.NumberFormat('th-TH').format(student.total_purchase)}</span>
                      : <span className="text-muted-foreground">-</span>
                    }
                  </TableCell>
                  <TableCell>
                    <Badge variant={config.variant} className={config.className}>
                      {config.label}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <StudentTeacherHistoryModal
        studentCode={selectedStudent}
        currentTeacherCode={teacherCode}
        onClose={() => setSelectedStudent(null)}
      />
    </div>
  );
};
